import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import { Lines } from "@/components/marketing/Lines";
import { BODY_SMALL } from "@/components/marketing/marketingType";
import {
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Card } from "@/content/landing/types";
import { LandingImage, LandingSection } from "../LandingPrimitives";
import type { Cards } from "./cardsShared";
import { FLOW_MEASURE, flowSection } from "./landingRun";

/**
 * `.eth-sourc_box`: the ethics page's opening panel. The block's title and
 * lines stand on the left, on their own measure; its cards stack on the right,
 * each a white tile with a small mark over its title and line. Below the wide
 * step the two columns stack and the cards centre.
 */
const BOX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "64px",
  width: 1,
  p: "48px",
  borderRadius: "32px",
  backgroundImage:
    "linear-gradient(251deg, rgba(242, 244, 255, 0.65), rgba(255, 255, 255, 0))",
  [NOT_WIDE]: { flexDirection: "column", p: "32px" },
  [MID]: {
    backgroundImage: "linear-gradient(2deg, #f0f5f8, rgba(255, 255, 255, 0))",
  },
  [TINY]: { gap: "16px", borderRadius: "24px", p: "8px 19.2px 19.2px" },
} satisfies SxProps<Theme>;

/** `.eth-sourc-box_left` */
const COPY = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "12px",
  width: 1,
  maxWidth: "430px",
  [NOT_WIDE]: { maxWidth: "none" },
  [TINY]: { gap: "16px" },
} satisfies SxProps<Theme>;

/** `.h3_new.is-eth-sourc`: the section's heading, a size under `.h2_new`, 12 further off its lines. */
const TITLE = {
  color: marketingPalette.ink,
  fontSize: 32,
  lineHeight: "41.6px",
  fontWeight: 500,
  letterSpacing: "-0.04em",
  mb: "12px",
  [TINY]: { fontSize: 24, lineHeight: "32px", mb: 0 },
} satisfies SxProps<Theme>;

/** `.eth-sourc-box_right` */
const TILES = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "16px",
  width: 1,
  [NOT_WIDE]: { alignItems: "center", textAlign: "center", gap: "24px" },
  [TINY]: { gap: "20.8px" },
} satisfies SxProps<Theme>;

/** `.eth-sourc-header_item`: white, on a soft shadow, its parts 8 apart. */
const TILE = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  width: 1,
  p: "24px 28px",
  borderRadius: "16px",
  backgroundColor: "common.white",
  boxShadow: "0 12px 30px -20px rgba(180, 195, 205, 0.7)",
  [NOT_WIDE]: { alignItems: "flex-start", textAlign: "left" },
  [MID]: { p: "20px" },
} satisfies SxProps<Theme>;

/** `.eth-sourc-header_icon`: 32 square, 8 further off the title. */
const MARK = {
  width: "32px",
  height: "32px",
  mb: "8px",
} satisfies SxProps<Theme>;

/** `.eth-sourc-header_title` */
const TILE_TITLE = {
  color: marketingPalette.ink,
  fontSize: 18.08,
  lineHeight: "21.696px",
  fontWeight: 500,
} satisfies SxProps<Theme>;

/** `.eth-sourc-header_body` */
const TILE_LINE = {
  fontSize: 14.08,
  lineHeight: "21.12px",
  opacity: 0.9,
} satisfies SxProps<Theme>;

export default function CardsAside({ block }: { block: Cards }) {
  return (
    <LandingSection {...flowSection(block)}>
      <Box sx={FLOW_MEASURE}>
        <Box sx={BOX}>
          <Box sx={COPY}>
            {block.title ? (
              <Typography component="h3" sx={TITLE}>
                <Lines text={block.title} />
              </Typography>
            ) : null}
            {block.body?.split("\n").map((line, index) => (
              <Typography key={index} sx={BODY_SMALL}>
                {line}
              </Typography>
            ))}
          </Box>
          <Box sx={TILES}>
            {block.items.map((card, index) => (
              <Tile key={index} card={card} />
            ))}
          </Box>
        </Box>
      </Box>
    </LandingSection>
  );
}

function Tile({ card }: { card: Card }) {
  return (
    <Box sx={TILE}>
      {card.icon ? <LandingImage src={card.icon} sx={MARK} /> : null}
      <Typography sx={TILE_TITLE}>
        <Lines text={card.title} />
      </Typography>
      {card.body ? <Typography sx={TILE_LINE}>{card.body}</Typography> : null}
    </Box>
  );
}
